import { useRuntimeStatus } from "./api/runtimeStatusContext";
import { usePositionActions } from "./api/usePositionActions";
import { OperationToasts } from "./components/OperationToasts";
import { api } from "./api/client";
import type { OpeningRetryPrefill, PoolsResponse } from "@auto-pools/core";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { EventStreamProvider } from "./api/EventStream";
import { RuntimeStatusProvider } from "./api/RuntimeStatusProvider";
import { AuthProvider } from "./auth/AuthProvider";
import { useAuth } from "./auth/authContext";
import { LoginPage } from "./auth/LoginPage";
import { GlobalStatus } from "./components/GlobalStatus";
import { AutopilotProvider } from "./components/AutopilotProvider";
import { AutopilotPill } from "./components/AutopilotPill";
import { AutopilotBanner } from "./components/AutopilotBanner";
import { HistoryPage } from "./pages/HistoryPage";
import { PoolsPage } from "./pages/PoolsPage";
import { PositionsPage } from "./pages/PositionsPage";
import { AutopilotPage } from "./pages/AutopilotPage";
import { SettingsPage } from "./pages/SettingsPage";
import type { Pool } from "./types";
import { OpenWizard } from "./wizard/OpenWizard";

type Page = "pools" | "positions" | "autopilot" | "history" | "settings";
const nav: { id: Page; label: string }[] = [
  { id: "positions", label: "My positions" },
  { id: "pools", label: "Pools" },
  { id: "autopilot", label: "Autopilot" },
  { id: "history", label: "History" },
  { id: "settings", label: "Settings" },
];

function WalletFence({ onChange }: { onChange: (identity: string) => void }) {
  const runtime = useRuntimeStatus();
  // Unknown ("") until the first status answer: My positions, the landing page, starts its read
  // only then, so the identity change that follows cannot void that read.
  const identity =
    runtime.data || !runtime.loading
      ? JSON.stringify([runtime.data?.wallet.address, runtime.data?.wallet.generation])
      : "";
  useEffect(() => {
    onChange(identity);
  }, [identity, onChange]);
  return null;
}
function Terminal() {
  const auth = useAuth();
  const [page, setPage] = useState<Page>("positions");
  const [refresh, setRefresh] = useState(0);
  const [historyTab, setHistoryTab] = useState<"closed" | "operations">("closed");
  const [creation, setCreation] = useState<{
    pool: Pool;
    referenceAt?: string;
    prefill?: OpeningRetryPrefill;
  } | null>(null);
  const [walletIdentity, setWalletIdentity] = useState("");
  const actions = usePositionActions(
    auth.authenticated,
    page === "positions" && !creation,
    refresh,
    walletIdentity,
  );
  const [showPosition, setShowPosition] = useState<string | null>(null);
  const [restartError, setRestartError] = useState<string | null>(null);
  const sessionActive = useRef(auth.authenticated);
  sessionActive.current = auth.authenticated;
  const currentWallet = useRef(walletIdentity);
  currentWallet.current = walletIdentity;
  const navigationGeneration = useRef(0),
    restartFlights = useRef(new Set<string>());
  useEffect(() => {
    if (!auth.authenticated) {
      navigationGeneration.current++;
      restartFlights.current.clear();
      setRestartError(null);
      setShowPosition(null);
    }
  }, [auth.authenticated]);
  useEffect(() => {
    if (!showPosition || page !== "positions" || creation) return;
    const scroll = () => {
      const target = document.getElementById(`position-heading-${showPosition}`);
      if (!target) return false;
      target.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        block: "center",
      });
      target.focus({ preventScroll: true });
      setShowPosition(null);
      return true;
    };
    if (scroll()) return;
    const observer = new MutationObserver(scroll);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
    };
  }, [showPosition, page, creation]);
  const restart = useCallback(
    async (id: string) => {
      if (creation || restartFlights.current.size > 0) return;
      const action = actions.actions[id],
        draft = action?.restart;
      if (!draft || action.operation?.state !== "completed") return;
      const generation = navigationGeneration.current,
        wallet = walletIdentity;
      restartFlights.current.add(id);
      try {
        const response = await api<PoolsResponse>(
          `/pools?search=${encodeURIComponent(draft.request.poolAddress)}`,
          { cache: "no-store" },
        );
        const pool = response.items.find(
          (p) =>
            p.address === draft.request.poolAddress &&
            p.tokenA.mint === draft.tokenA.mint &&
            p.tokenB.mint === draft.tokenB.mint &&
            p.tokenA.decimals === draft.tokenA.decimals &&
            p.tokenB.decimals === draft.tokenB.decimals,
        );
        if (pool?.price == null)
          throw new Error(
            "The pool's current catalog data is unavailable. Refresh Pools to start a new setup.",
          );
        if (
          sessionActive.current &&
          generation === navigationGeneration.current &&
          currentWallet.current === wallet &&
          actions.consumeRestart(id)
        ) {
          navigationGeneration.current++;
          setCreation({ pool, prefill: draft });
          setPage("pools");
        }
      } catch (error) {
        if (sessionActive.current && generation === navigationGeneration.current)
          setRestartError(error instanceof Error ? error.message : "Pool unavailable");
      } finally {
        restartFlights.current.delete(id);
      }
    },
    [actions, creation, walletIdentity],
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuId = useId();
  const mobileMenu = useRef<HTMLDivElement>(null);
  const mobileMenuTrigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const show = () => {
      setMobileMenuOpen(true);
    };
    window.addEventListener("auto-pools:show-system-status", show);
    return () => {
      window.removeEventListener("auto-pools:show-system-status", show);
    };
  }, []);
  const deepLink = useRef(window.location.hash === "#autopilot");
  const returnToCatalog = useRef<{
    poolAddress: string;
    documentX: number;
    documentY: number;
    tableX: number;
  } | null>(null);

  useEffect(() => {
    // One server tick carries several named events (status and operations). Bumping a single
    // counter for all of them made every reader refetch once per event, so a quiet panel issued
    // two full requests every tick. Each topic now moves only the readers that follow it.
    const update = (event: Event) => {
      // Runtime status follows its own event inside its provider, so counting it here as well
      // would move every data reader a second time for the same tick.
      if ((event as CustomEvent<unknown>).detail === "status") return;
      setRefresh((value) => value + 1);
    };
    window.addEventListener("auto-pools:server-event", update);
    return () => {
      window.removeEventListener("auto-pools:server-event", update);
    };
  }, []);

  useEffect(() => {
    if (auth.loading || auth.authenticated) return;
    setPage("positions");
    setWalletIdentity("");
    setHistoryTab("closed");
    setCreation(null);
    returnToCatalog.current = null;
  }, [auth.authenticated, auth.loading]);

  useEffect(() => {
    // The Telegram panel's "Open terminal" button (design 49) is a plain url button to
    // `AUTO_POOLS_ORIGIN` + `#autopilot` — there is no router, so the deep link is read once on
    // mount (into `deepLink`) and the hash cleared immediately after.
    if (window.location.hash !== "#autopilot") return;
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, []);

  useEffect(() => {
    // Applied once the session is known: a browser without one signs in first, and the
    // unauthenticated reset above would otherwise have sent it back to Pools.
    if (auth.loading || !auth.authenticated || !deepLink.current) return;
    deepLink.current = false;
    setPage("autopilot");
  }, [auth.authenticated, auth.loading]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const close = (returnFocus: boolean) => {
      setMobileMenuOpen(false);
      if (returnFocus) mobileMenuTrigger.current?.focus();
    };
    const closeOnPointer = (event: PointerEvent) => {
      if (!mobileMenu.current?.contains(event.target as Node)) close(true);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (mobileMenu.current?.querySelector(".status-popover")) return;
      event.preventDefault();
      close(true);
    };
    document.addEventListener("pointerdown", closeOnPointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnPointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileMenuOpen]);

  function navigate(nextPage: Page) {
    navigationGeneration.current++;
    returnToCatalog.current = null;
    setCreation(null);
    setPage(nextPage);
    setMobileMenuOpen(false);
  }

  function openAutopilotPosition(target: { operationId?: string; positionId?: string }) {
    // An operation target (D6, review round 1) has nothing to show on My positions — a completed
    // close is excluded there. History's Operations tab is the existing view that can show any
    // operation; it does not select the specific row, only the tab, since doing that would need
    // HistoryPage's own operation-id filtering, out of this phase's file scope.
    if (target.operationId) {
      navigate("history");
      setHistoryTab("operations");
      return;
    }
    navigate("positions");
    if (target.positionId) setShowPosition(target.positionId);
  }

  function openCreation(pool: Pool, referenceAt: string | undefined, origin: HTMLButtonElement) {
    navigationGeneration.current++;
    returnToCatalog.current = {
      poolAddress: pool.address,
      documentX: window.scrollX,
      documentY: window.scrollY,
      tableX: origin.closest<HTMLElement>(".table-scroll")?.scrollLeft ?? 0,
    };
    setCreation({ pool, referenceAt });
  }

  function backToPools() {
    const restore = returnToCatalog.current;
    returnToCatalog.current = null;
    setCreation(null);
    setPage("pools");
    window.requestAnimationFrame(() => {
      if (!restore) return;
      const table = document.querySelector<HTMLElement>(".table-scroll");
      if (table) table.scrollLeft = restore.tableX;
      const origin = [...document.querySelectorAll<HTMLButtonElement>("[data-open-pool]")].find(
        (button) => button.dataset.openPool === restore.poolAddress,
      );
      origin?.focus({ preventScroll: true });
      window.scrollTo({ left: restore.documentX, top: restore.documentY });
    });
  }

  if (auth.loading)
    return (
      <main className="center-state">
        <span className="spinner" />
        Checking secure session…
      </main>
    );
  if (!auth.authenticated) return <LoginPage />;

  return (
    <EventStreamProvider>
      <RuntimeStatusProvider>
        <AutopilotProvider>
          <WalletFence onChange={setWalletIdentity} />
          <div className={`app-shell ${creation ? "create-mode" : ""}`}>
            <header className="top-nav">
              <button
                className="brand"
                onClick={() => {
                  navigate("pools");
                }}
                aria-label="Auto Pools, go to pools"
              >
                <svg
                  className="brand-mark"
                  viewBox="0 0 32 32"
                  fill="none"
                  role="img"
                  aria-label="Auto Pools"
                >
                  <path
                    d="M13 5H7.5A2.5 2.5 0 0 0 5 7.5v17A2.5 2.5 0 0 0 7.5 27H13v-5H10V10h3z"
                    fill="#f0a92c"
                  />
                  <path
                    d="M19 5h5.5A2.5 2.5 0 0 1 27 7.5v17a2.5 2.5 0 0 1-2.5 2.5H19v-5h3V10h-3z"
                    fill="#f0a92c"
                  />
                  <circle cx="16" cy="16" r="5" fill="#f04438" />
                </svg>
                <b>
                  <span className="desktop-brand-label">Auto Pools</span>
                  <span className="mobile-brand-label">
                    {nav.find((item) => item.id === page)?.label}
                  </span>
                </b>
              </button>
              <div className="mobile-nav-control" ref={mobileMenu}>
                <button
                  ref={mobileMenuTrigger}
                  type="button"
                  className="mobile-menu-trigger"
                  aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                  aria-expanded={mobileMenuOpen}
                  aria-controls={mobileMenuId}
                  onClick={() => {
                    setMobileMenuOpen((open) => !open);
                  }}
                >
                  <svg viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M3 6h14M3 10h14M3 14h14" />
                  </svg>
                </button>
                <div
                  id={mobileMenuId}
                  className={`mobile-nav-panel ${mobileMenuOpen ? "open" : ""}`}
                >
                  <nav aria-label="Main navigation">
                    {nav.map((item) => (
                      <button
                        key={item.id}
                        className={page === item.id ? "active" : ""}
                        onClick={() => {
                          navigate(item.id);
                          mobileMenuTrigger.current?.focus();
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </nav>
                  {auth.viewer && (
                    <span
                      className="viewer-pill"
                      title="Read-only session: every action is hidden and refused by the server"
                    >
                      <svg viewBox="0 0 14 14" aria-hidden="true">
                        <rect x="3" y="6.5" width="8" height="5.5" rx="1.2" />
                        <path d="M4.8 6.5V4.8a2.2 2.2 0 0 1 4.4 0v1.7" />
                      </svg>
                      Read-only
                    </span>
                  )}
                  <AutopilotPill
                    onOpen={() => {
                      navigate("autopilot");
                    }}
                  />
                  <GlobalStatus
                    onNavigate={(destination) => {
                      if (destination === "history") setHistoryTab("operations");
                      navigate(destination);
                    }}
                  />
                  {
                    // Reuses GlobalStatus's own `.status-banner` chrome (full-bleed via the flex
                    // `order: 10` trick), which only works as a flex child of `.top-nav` — placing it
                    // outside `.mobile-nav-panel` would bleed off the unpadded `.app-shell` instead.
                  }
                  <AutopilotBanner
                    onOpenPosition={openAutopilotPosition}
                    onOpenAutopilot={() => {
                      navigate("autopilot");
                    }}
                    onOpenSettings={() => {
                      navigate("settings");
                    }}
                  />
                  <button
                    className="button ghost logout"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      void auth.logout();
                    }}
                  >
                    Sign out
                  </button>
                </div>
              </div>
            </header>
            <main className={`page-wrap ${creation ? "create-page-wrap" : ""}`}>
              {page === "pools" && (
                <PoolsPage refresh={refresh} visible={!creation} onOpen={openCreation} />
              )}
              {creation && (
                <OpenWizard
                  pool={creation.pool}
                  prefill={creation.prefill}
                  referenceAt={creation.referenceAt}
                  onClose={backToPools}
                  onPositions={() => {
                    navigate("positions");
                  }}
                />
              )}
              {page === "positions" && walletIdentity !== "" && (
                <PositionsPage
                  actions={actions}
                  refresh={refresh}
                  onBrowsePools={() => {
                    navigate("pools");
                  }}
                  onOpenSettings={() => {
                    navigate("settings");
                  }}
                  onOpenAutopilot={() => {
                    navigate("autopilot");
                  }}
                />
              )}
              {page === "autopilot" && <AutopilotPage onOpenPosition={openAutopilotPosition} />}
              {page === "history" && (
                <HistoryPage refresh={refresh} tab={historyTab} onTab={setHistoryTab} />
              )}
              {page === "settings" && <SettingsPage />}
            </main>
            {restartError && (
              <p className="inline-alert error" role="alert">
                {restartError}
              </p>
            )}
            <OperationToasts
              controller={actions}
              onShow={(id) => {
                navigate("positions");
                setShowPosition(id);
              }}
              onHistory={(tab) => {
                navigate("history");
                setHistoryTab(tab);
              }}
              onRestart={(id) => void restart(id)}
            />
          </div>
        </AutopilotProvider>
      </RuntimeStatusProvider>
    </EventStreamProvider>
  );
}

export function App() {
  return (
    <AuthProvider>
      <Terminal />
    </AuthProvider>
  );
}
